import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import {
  estimateSuffix,
  measureDescription,
  type Measure,
} from "../../components/index.ts";
import type { IdentityMcpSummary } from "./use-identity-mcp.ts";
import { quantityMeasure } from "./vocab.ts";

/**
 * Identity MCP latency percentiles (task 7.3; requirement 13.3 "latency percentiles").
 *
 * The summary carries exactly three handler-latency percentiles — p50, p95, p99 — each a `Quantity`
 * that says whether it is exact or an estimate (percentiles are typically histogram estimates, so
 * these usually read "est."). They are rendered as three tiles, each labelled with its confidence
 * and shown in milliseconds; a null percentile (no data) is a text em dash styled unverifiable
 * (grey, never green; design 12.3), never a fabricated zero.
 */

type Summary = IdentityMcpSummary;
type Quantity = components["schemas"]["Quantity"];

interface PercentileSpec {
  readonly key: "latency_p50_ms" | "latency_p95_ms" | "latency_p99_ms";
  readonly title: string;
}

const PERCENTILES: readonly PercentileSpec[] = [
  { key: "latency_p50_ms", title: "Latency p50" },
  { key: "latency_p95_ms", title: "Latency p95" },
  { key: "latency_p99_ms", title: "Latency p99" },
];

const MS = (v: number): string => v.toLocaleString(undefined, { maximumFractionDigits: 1 });

export function LatencyTiles(props: { summary: Summary }): ReactNode {
  return (
    <section className="imcp-latency" aria-label="Identity MCP latency">
      <h2 className="imcp-section__title">Operation latency</h2>
      <div className="imcp-latency__grid">
        {PERCENTILES.map((spec) => (
          <LatencyTile key={spec.key} spec={spec} quantity={props.summary[spec.key]} />
        ))}
      </div>
    </section>
  );
}

function LatencyTile(props: { spec: PercentileSpec; quantity: Quantity }): ReactNode {
  const { spec, quantity } = props;
  const measure: Measure = quantityMeasure(quantity);
  const value = quantity.value;

  const display = value === null ? "n/a" : `${MS(value)} ms${estimateSuffix(measure)}`;
  const a11y =
    value === null
      ? `${spec.title}: no data (unverifiable).`
      : `${spec.title}: ${MS(value)} milliseconds, ${measureDescription(measure)}.`;

  return (
    <div className="imcp-tile" aria-label={a11y}>
      <span className="imcp-tile__title">{spec.title}</span>
      <span className="imcp-tile__value" data-measure={measure}>
        {display}
      </span>
    </div>
  );
}
